import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Mail, Globe, Shield, AlertTriangle, FileBadge, Award, History, ScrollText } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function UserDetailsDrawer({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!userId) return;
    api
      .get(`/admin/details/user/${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load"));
  }, [userId]);

  if (!userId) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex justify-end"
      onClick={onClose}
      data-testid="user-details-drawer"
    >
      <div
        className="bg-ec-ink border-l border-white/10 w-full max-w-xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-ec-ink border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="eyebrow text-ec-emerald">User Details</div>
            <h3 className="font-display text-xl font-bold text-white mt-1">
              {data?.user?.full_name || data?.user?.organization_name || "Loading…"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white p-1.5"
            data-testid="user-details-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {err && (
          <div className="p-6 text-ec-error text-sm" data-testid="user-details-error">
            {err}
          </div>
        )}

        {!data && !err && (
          <div className="p-6 text-zinc-500 text-sm">Loading…</div>
        )}

        {data && (
          <div className="p-6 space-y-6">
            <section data-testid="user-details-profile">
              <div className="eyebrow text-zinc-500 mb-3">Profile</div>
              <Row icon={Mail} label="Email" value={data.user.email} />
              <Row icon={Shield} label="Role" value={<Badge className="bg-white/10 border-white/10 rounded-sm">{data.user.role}</Badge>} />
              <Row icon={Globe} label="Country" value={data.user.country || "—"} />
              <Row label="EthicCred ID" value={<span className="font-mono-plex text-xs">{data.user.ethic_id || data.user.org_id || data.user.institution_id || "—"}</span>} />
              <Row label="Status" value={data.user.suspended ? <Badge className="bg-ec-error/20 text-ec-error border-ec-error/40 rounded-sm">Suspended</Badge> : <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 rounded-sm">Active</Badge>} />
              <Row label="Verified" value={data.user.verified_email ? "Yes" : "No"} />
              <Row label="MFA" value={data.user.mfa_enabled ? "Enabled" : "Disabled"} />
              <Row label="Joined" value={<span className="font-mono-plex text-xs">{(data.user.created_at || "").slice(0, 19).replace("T", " ")}</span>} />
            </section>

            <section data-testid="user-details-stats">
              <div className="eyebrow text-zinc-500 mb-3">Activity</div>
              <div className="grid grid-cols-2 gap-px bg-white/10">
                <Stat icon={Award} label="Certs received" value={data.stats.certs_received} />
                <Stat icon={FileBadge} label="Certs issued" value={data.stats.certs_issued} />
                <Stat label="Employee records" value={data.stats.employee_records} />
                <Stat label="Student records" value={data.stats.student_records} />
                <Stat icon={AlertTriangle} label="Disputes" value={data.stats.disputes} dangerIf={data.stats.disputes > 0} />
                <Stat label="Fraud score" value={data.fraud?.score ?? "—"} dangerIf={(data.fraud?.score || 0) >= 70} />
              </div>
            </section>

            {data.last_logins?.length > 0 && (
              <section data-testid="user-details-logins">
                <div className="eyebrow text-zinc-500 mb-3 flex items-center gap-2">
                  <History className="w-3.5 h-3.5" /> Last logins
                </div>
                <ul className="space-y-1.5 text-xs text-zinc-400 font-mono-plex">
                  {data.last_logins.map((l, i) => (
                    <li key={`${l.created_at || ""}-${l.ip || ""}-${i}`} className="flex justify-between border-b border-white/5 pb-1">
                      <span>{(l.created_at || "").slice(0, 19).replace("T", " ")}</span>
                      <span className="text-zinc-500">{l.ip || ""}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {data.recent_audit?.length > 0 && (
              <section data-testid="user-details-audit">
                <div className="eyebrow text-zinc-500 mb-3 flex items-center gap-2">
                  <ScrollText className="w-3.5 h-3.5" /> Recent audit
                </div>
                <ul className="space-y-1.5 text-xs text-zinc-400 font-mono-plex">
                  {data.recent_audit.map((a, i) => (
                    <li key={`${a.timestamp || ""}-${a.action || ""}-${i}`} className="border-b border-white/5 pb-1">
                      <span className="text-white">{a.action}</span>
                      <span className="text-zinc-500 ml-2">{(a.timestamp || "").slice(0, 19).replace("T", " ")}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-white/5">
      <div className="w-32 flex items-center gap-1.5 text-xs eyebrow text-zinc-500">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="flex-1 text-sm text-white break-all">{value}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, dangerIf }) {
  return (
    <div className={`p-3 bg-ec-ink ${dangerIf ? "border-l-2 border-ec-error" : ""}`}>
      <div className="flex items-center gap-1.5 eyebrow text-zinc-500">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="font-display text-2xl font-black text-white mt-1">{value}</div>
    </div>
  );
}
